import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import landing_ai from '../../assets/landing_page/ai.svg';
import landing_profile from '../../assets/landing_page/profile.svg';
import landing_howdoesitwork from '../../assets/landing_page/howdoesitwork.svg';
import ParticleBackground from '../../components/ParticleBackground';
import HeroNetworkCanvas from '../../components/landing/HeroNetworkCanvas';
import { globalAnimationStyles } from '../../styles/theme';
import { getCommonContainerStyles } from '../../styles/commonStyles';
import {
  useMobile,
  useThemeCssVars,
} from '../../styles/commonHooks';
import { API_URL } from '../../config';

// ─── Color tokens ─────────────────────────────────────────────────────────────
const COLORS = {
  cgGold: '#88712B',
  indigo600: '#6366f1',
  indigo700: '#4338ca',
  indigo800: '#3730a3',
  indigo900: '#5b5fc7',
  purple500: '#8b5cf6',
  panelBg: 'rgba(255,255,255,0.52)',
  panelBorder: 'rgba(99,102,241,0.13)',
  shadow: {
    indigo: {
      btn: 'rgba(102,126,234,0.32)',
      hoverCard: 'rgba(102,126,234,0.2)',
      activeCard: 'rgba(102,126,234,0.15)',
      iconGlow: 'rgba(102,126,234,0.25)',
    },
    guru: {
      hoverCard: 'rgba(136,113,43,0.25)',
      activeCard: 'rgba(136,113,43,0.18)',
    },
    text: 'rgba(0,0,0,0.65)',
    icon: 'rgba(0,0,0,0.08)',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getIconFilter = (isGuruDomain, theme) => {
  if (isGuruDomain) return `sepia(1) saturate(2) hue-rotate(5deg) brightness(0.8)`;
  if (theme === 'dark') return `brightness(1.08) drop-shadow(0 0 3px ${COLORS.shadow.indigo.iconGlow})`;
  return `drop-shadow(0 1px 3px ${COLORS.shadow.icon})`;
};

const getCardShadow = (isGuruDomain, type = 'hover') =>
  isGuruDomain
    ? (type === 'hover' ? `0 8px 24px ${COLORS.shadow.guru.hoverCard}` : `0 4px 12px ${COLORS.shadow.guru.activeCard}`)
    : (type === 'hover' ? `0 8px 24px ${COLORS.shadow.indigo.hoverCard}` : `0 4px 12px ${COLORS.shadow.indigo.activeCard}`);

const makeCardHandlers = (isGuruDomain, isSamsungBrowser, cardBaseBg, cardHoverBg, cardActiveBg) => ({
  onMouseEnter(e) {
    const el = e.currentTarget;
    el.style.transform = 'translateY(-1px)';
    el.style.background = cardHoverBg;
    el.style.backdropFilter = isSamsungBrowser ? 'none' : 'blur(14px)';
    el.style.WebkitBackdropFilter = isSamsungBrowser ? 'none' : 'blur(14px)';
    el.style.boxShadow = getCardShadow(isGuruDomain, 'hover');
  },
  onMouseLeave(e) {
    const el = e.currentTarget;
    el.style.transform = 'translateY(0)';
    el.style.background = cardBaseBg;
    el.style.backdropFilter = isSamsungBrowser ? 'none' : 'blur(18px)';
    el.style.WebkitBackdropFilter = isSamsungBrowser ? 'none' : 'blur(18px)';
    el.style.boxShadow = 'none';
  },
  onMouseDown(e) {
    const el = e.currentTarget;
    el.style.transform = 'translateY(0) scale(0.995)';
    el.style.boxShadow = getCardShadow(isGuruDomain, 'active');
    el.style.background = cardActiveBg;
  },
  onMouseUp(e) {
    const el = e.currentTarget;
    el.style.transform = 'translateY(-1px)';
    el.style.boxShadow = getCardShadow(isGuruDomain, 'hover');
    el.style.background = cardHoverBg;
  },
  onTouchStart(e) {
    const el = e.currentTarget;
    el.setAttribute('data-active', 'true');
    el.style.transform = 'translateY(0) scale(0.995)';
    el.style.boxShadow = getCardShadow(isGuruDomain, 'active');
    el.style.background = cardActiveBg;
  },
  onTouchEnd(e) {
    const el = e.currentTarget;
    el.removeAttribute('data-active');
    el.style.transform = 'translateY(0)';
    el.style.boxShadow = 'none';
    el.style.background = cardBaseBg;
    el.style.backdropFilter = isSamsungBrowser ? 'none' : 'blur(18px)';
    el.style.WebkitBackdropFilter = isSamsungBrowser ? 'none' : 'blur(18px)';
  },
});

// ─── Translations ─────────────────────────────────────────────────────────────
const getPageText = (lang, isGuruDomain) => {
  const appName = isGuruDomain ? 'Connect Guru' : 'Network Assistant';
  const isFr = lang === 'fr';
  const isRu = lang === 'ru';

  let heroTitle;
  let heroLead;
  if (isGuruDomain) {
    heroTitle = isRu
      ? 'Соберите профиль.\nНайдите нужных партнёров.'
      : isFr
        ? 'Créez votre présence.\nTrouvez les bons partenaires.'
        : 'Build your presence.\nFind aligned partners.';
    heroLead = isRu
      ? `${appName} — ИИ для деловых интро и точных совпадений.`
      : isFr
        ? `${appName} relie vos objectifs à des profils alignés pour le business.`
        : `${appName} connects your goals with aligned profiles for business networking.`;
  } else {
    heroTitle = isRu
      ? 'Создайте профиль.\nНайдите нужные контакты.'
      : isFr
        ? 'Créez votre profil.\nTrouvez les bons contacts.'
        : 'Build your profile.\nFind the right contacts.';
    heroLead = isRu
      ? `${appName} превращает вашу цель в контекстные интро для делового нетворкинга в Европе.`
      : isFr
        ? `${appName} transforme votre objectif en intros contextuelles pour le networking professionnel en Europe.`
        : `${appName} turns your goal into contextual intros for business networking across Europe.`;
  }

  return {
    h1: appName,
    heroTitle,
    heroLead,
    subtitle: isRu
      ? 'Идеальные связи с помощью ИИ'
      : isFr
        ? "Trouvez des connexions parfaites avec l'IA"
        : 'Find perfect connections with AI assistance',
    description: isGuruDomain
      ? 'Connect Guru uses artificial intelligence to quickly find the most suitable people for business and investments.'
      : isRu
        ? 'ИИ помогает собрать профиль и подобрать подходящих людей для бизнеса и инвестиций.'
        : isFr
          ? 'Notre IA pour créer votre profil et matcher avec les bonnes personnes, dans la vie pro'
          : 'Network assistant uses artificial intelligence to quickly find the most suitable people for business and investments.',
    eventsTitle: isRu ? 'События и вечеринки' : isFr ? 'Événements & soirées' : 'Events & Parties',
    eventsDesc: isRu
      ? 'Закрытые мероприятия рядом с вами'
      : isFr
        ? 'Découvrez les événements exclusifs près de chez vous'
        : 'Discover exclusive events near you',
    eventsCtaHint: isRu ? 'Смотреть афишу' : isFr ? 'Voir les événements' : 'Browse events',
    cards: [
      {
        icon: landing_ai,
        title: isRu ? 'Чат с ИИ' : isFr ? "Chat avec l'IA" : 'Chat with AI',
        desc: isRu
          ? 'Профиль за пару шагов'
          : isFr
            ? 'Créez un profil parfait en quelques clics'
            : 'Create a perfect profile in few clicks',
        path: '/chat',
      },
      {
        icon: landing_profile,
        title: isRu ? 'Мой профиль' : isFr ? 'Mon Profil' : 'My Profile',
        desc: isRu
          ? 'Данные и настройки'
          : isFr
            ? 'Gérez vos informations et préférences'
            : 'Manage your information & preferences',
        path: '/profile',
      },
      {
        icon: landing_howdoesitwork,
        title: isRu ? 'Как это работает' : isFr ? 'Comment ça marche?' : 'How It Works',
        desc: isRu
          ? 'Как подбираются совпадения'
          : isFr
            ? 'Découvrez le processus de matching'
            : 'Discover the matching process',
        path: '/how-it-works',
      },
    ],
    stats: {
      users: isRu ? 'Пользователи' : isFr ? 'Utilisateurs' : 'Users',
      matches: isRu ? 'Связи' : isFr ? 'Connexions établies' : 'Connections made',
      events: isRu ? 'События' : isFr ? 'Événements passés' : 'Events held',
    },
  };
};

// ─── Animated number counter hook ────────────────────────────────────────────
function useCountUp(target, duration = 1800, started = false) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!started || target === 0) { setValue(0); return; }
    const startTime = performance.now();
    const startVal = 0;

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setValue(Math.round(startVal + (target - startVal) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, started]);

  return value;
}

// ─── Single stat card ─────────────────────────────────────────────────────────
function StatCard({ value, label, icon, accent, isGuruDomain, isMobile, theme, started }) {
  const animated = useCountUp(value, 1600, started);

  const formatNum = (n) => {
    if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1).replace('.0', '') + 'K';
    return n.toString();
  };

  const isDark = theme === 'dark';
  const cardBg = isDark
    ? 'rgba(255,255,255,0.04)'
    : 'rgba(255,255,255,0.62)';
  const cardBorder = isDark
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(99,102,241,0.12)';
  const numColor = isGuruDomain ? COLORS.cgGold : accent;
  const labelColor = isDark ? 'rgba(255,255,255,0.55)' : 'rgba(30,20,60,0.5)';

  return (
    <div style={{
      background: cardBg,
      border: `1px solid ${cardBorder}`,
      borderRadius: isMobile ? 16 : 20,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      padding: isMobile ? '20px 16px' : '32px 24px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: isMobile ? 6 : 10,
      flex: 1,
      minWidth: 0,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: isDark
        ? '0 4px 24px rgba(0,0,0,0.3)'
        : '0 4px 24px rgba(99,102,241,0.08)',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute',
        top: -20,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        height: 60,
        background: isGuruDomain
          ? `radial-gradient(ellipse, rgba(136,113,43,0.18) 0%, transparent 70%)`
          : `radial-gradient(ellipse, rgba(99,102,241,0.14) 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Icon */}
      <div style={{
        width: isMobile ? 28 : 36,
        height: isMobile ? 28 : 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: numColor,
        opacity: 0.7,
      }}>
        {icon}
      </div>

      {/* Number */}
      <div style={{
        fontSize: isMobile ? '2rem' : '2.8rem',
        fontWeight: 800,
        lineHeight: 1,
        letterSpacing: '-0.03em',
        color: numColor,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {formatNum(animated)}
      </div>

      {/* Label */}
      <div style={{
        fontSize: isMobile ? '0.72rem' : '0.82rem',
        fontWeight: 600,
        color: labelColor,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
        lineHeight: 1.3,
      }}>
        {label}
      </div>
    </div>
  );
}

// ─── Stats section ────────────────────────────────────────────────────────────
function StatsCounter({ lang, isGuruDomain, isMobile, theme, themeVars, outerMarginBottom }) {
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);
  const text = getPageText(lang, isGuruDomain);

  useEffect(() => {
    fetch(`${API_URL}/api/public/stats`)
      .then(r => r.json())
      .then(d => {
        if (d.show) setStats(d);
      })
      .catch(() => {});
  }, []);

  // Intersection Observer — запускаем анимацию когда секция видима
  useEffect(() => {
    if (!stats || !sectionRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          setTimeout(() => setStarted(true), 100);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [stats]);

  if (!stats) return null;

  const accent = themeVars.btnBg || COLORS.indigo600;

  const IconUsers = (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3.5"/>
      <path d="M2 21v-1.5a5.5 5.5 0 0 1 5.5-5.5h3A5.5 5.5 0 0 1 16 19.5V21"/>
      <path d="M16 3.5a3.5 3.5 0 0 1 0 7"/>
      <path d="M22 21v-1.5a5.5 5.5 0 0 0-4-5.3"/>
    </svg>
  );
  const IconMatches = (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
      <path d="m15 5 4 4"/>
    </svg>
  );
  const IconEvents = (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01"/>
    </svg>
  );

  const items = [
    { key: 'users',   value: stats.users,   icon: IconUsers,   label: text.stats.users   },
    { key: 'matches', value: stats.matches,  icon: IconMatches, label: text.stats.matches },
    { key: 'events',  value: stats.events,   icon: IconEvents,  label: text.stats.events  },
  ];

  return (
    <div
      ref={sectionRef}
      className="animate-on-mount"
      style={{
        marginBottom:
          outerMarginBottom !== undefined ? outerMarginBottom : isMobile ? 12 : 16,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}
    >
      {/* Cards row */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 12 : 16,
      }}>
        {items.map((item, i) => (
          <StatCard
            key={item.key}
            value={item.value}
            label={item.label}
            icon={item.icon}
            accent={accent}
            isGuruDomain={isGuruDomain}
            isMobile={isMobile}
            theme={theme}
            started={started}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage({ lang, theme, translations, token, isGuruDomain = false, isSamsungBrowser = false }) {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const heroBleedRef = useRef(null);
  const heroVisualRef = useRef(null);
  const [heroVisualHovered, setHeroVisualHovered] = useState(false);
  const [heroHoverFinePointer, setHeroHoverFinePointer] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const [heroReduceMotion, setHeroReduceMotion] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onHover = () => setHeroHoverFinePointer(mqHover.matches);
    const onReduce = () => setHeroReduceMotion(mqReduce.matches);
    mqHover.addEventListener('change', onHover);
    mqReduce.addEventListener('change', onReduce);
    return () => {
      mqHover.removeEventListener('change', onHover);
      mqReduce.removeEventListener('change', onReduce);
    };
  }, []);

  useEffect(() => {
    if (isMobile || !heroHoverFinePointer || heroReduceMotion) {
      setHeroVisualHovered(false);
    }
  }, [isMobile, heroHoverFinePointer, heroReduceMotion]);

  const themeVars = useThemeCssVars(theme, isSamsungBrowser);
  const containerStyles = getCommonContainerStyles(themeVars, isMobile);

  const iconFilter = getIconFilter(isGuruDomain, theme);
  const text = getPageText(lang, isGuruDomain);

  const isDarkTheme = theme === 'dark';
  const heroCanvasHoverBlur =
    !isMobile && heroHoverFinePointer && !heroReduceMotion && heroVisualHovered;

  const iosCardBaseBg = isDarkTheme
    ? isGuruDomain
      ? 'rgba(255,248,230,0.07)'
      : 'rgba(255,255,255,0.065)'
    : isGuruDomain
      ? 'rgba(255,252,245,0.88)'
      : 'rgba(255,255,255,0.78)';
  const iosCardHoverBg = isDarkTheme
    ? isGuruDomain
      ? 'rgba(255,248,230,0.10)'
      : 'rgba(255,255,255,0.095)'
    : isGuruDomain
      ? 'rgba(255,252,245,0.95)'
      : 'rgba(255,255,255,0.9)';
  const iosCardActiveBg = isDarkTheme
    ? isGuruDomain
      ? 'rgba(255,248,230,0.055)'
      : 'rgba(255,255,255,0.055)'
    : isGuruDomain
      ? 'rgba(255,252,245,0.82)'
      : 'rgba(248,248,252,0.96)';

  const cardHandlers = makeCardHandlers(
    isGuruDomain,
    isSamsungBrowser,
    iosCardBaseBg,
    iosCardHoverBg,
    iosCardActiveBg
  );

  const styles = {
    /** Только hero: на десктопе тянется на высоту первого экрана; статистика вынесена во «второй экран». */
    landingAboveFold: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      ...(isMobile
        ? {
            height: 'auto',
            maxHeight: 'none',
            minHeight: 0,
            overflowY: 'visible',
          }
        : {
            minHeight: 'calc(100dvh - 60px - 28px)',
            maxHeight: 'calc(100dvh - 60px - 20px)',
            overflowY: 'hidden',
          }),
      boxSizing: 'border-box',
      marginBottom: isMobile ? 6 : 10,
    },
    /** Одна «карточка» как StatCard: тот же фон, бордер, тень и верхнее свечение — без отдельных квадратов у текста/canvas */
    heroBleed: {
      width: '100%',
      alignSelf: 'stretch',
      minWidth: 0,
      position: 'relative',
      overflow: 'hidden',
      marginBottom: 0,
      boxSizing: 'border-box',
      borderRadius: isMobile ? 16 : 20,
      background: isDarkTheme ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.62)',
      border: `1px solid ${isDarkTheme ? 'rgba(255,255,255,0.08)' : 'rgba(99,102,241,0.12)'}`,
      backdropFilter: isSamsungBrowser ? 'none' : 'blur(16px)',
      WebkitBackdropFilter: isSamsungBrowser ? 'none' : 'blur(16px)',
      boxShadow: isDarkTheme
        ? '0 4px 24px rgba(0,0,0,0.3)'
        : '0 4px 24px rgba(99,102,241,0.08)',
      flex: isMobile ? '0 0 auto' : 1,
      minHeight: isMobile ? 'auto' : 0,
      display: 'flex',
      flexDirection: 'column',
    },
    /** Полный фон hero: canvas с космосом + сеткой; не перехватывает скролл/касания */
    heroCanvasHost: {
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 0,
      pointerEvents: 'none',
    },
    heroTopGlow: {
      position: 'absolute',
      top: -20,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '85%',
      height: 72,
      background: isGuruDomain
        ? 'radial-gradient(ellipse, rgba(136,113,43,0.2) 0%, transparent 72%)'
        : 'radial-gradient(ellipse, rgba(99,102,241,0.16) 0%, transparent 72%)',
      pointerEvents: 'none',
      zIndex: 1,
    },
    heroInnerRow: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: 'stretch',
      gap: isMobile ? 14 : 22,
      flex: isMobile ? '0 0 auto' : 1,
      minHeight: isMobile ? 'auto' : 0,
      minWidth: 0,
      padding: isMobile ? '16px 14px 18px' : '22px 20px 24px',
      boxSizing: 'border-box',
    },
    heroCopy: {
      flex: isMobile ? '0 0 auto' : '0 0 min(42%, 400px)',
      maxWidth: isMobile ? '100%' : 440,
      minWidth: 0,
      textAlign: 'left',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: isMobile ? '4px 4px 0 0' : '8px 12px 8px 4px',
      position: 'relative',
      zIndex: 1,
      pointerEvents: 'auto',
    },
    heroTitle: {
      margin: 0,
      fontSize: isMobile ? 'clamp(1.42rem, 5.4vw, 1.82rem)' : 'clamp(1.62rem, 2.75vw, 2.32rem)',
      fontWeight: 800,
      lineHeight: 1.14,
      letterSpacing: '-0.02em',
      color: isDarkTheme ? '#f8fafc' : themeVars.fg,
      whiteSpace: 'pre-line',
    },
    heroLead: {
      margin: 0,
      marginTop: isMobile ? 14 : 18,
      fontSize: isMobile ? '0.94rem' : '1.04rem',
      lineHeight: 1.55,
      fontWeight: 400,
      color: isDarkTheme ? 'rgba(241, 245, 249, 0.94)' : themeVars.smallText,
      maxWidth: '36rem',
    },
    heroVisual: {
      flex: isMobile ? '0 0 auto' : 1,
      minWidth: 0,
      width: '100%',
      minHeight: isMobile ? 'clamp(200px, 46dvh, 320px)' : 140,
      position: 'relative',
      overflow: 'hidden',
      pointerEvents: 'none',
    },
    /** Только ПК с мышью/touchpad: ловим hover над зоной графики, не трогая мобильный скролл */
    heroVisualHoverShield: {
      position: 'absolute',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      zIndex: 2,
      pointerEvents: 'auto',
      cursor: 'default',
    },
    /** Второй экран: сверху статистика (фото 2), ниже CTA «События» и три карточки */
    secondScreen: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 18 : 22,
      marginTop: 0,
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, system-ui, sans-serif',
    },
    secondScreenBelow: {
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 14 : 18,
    },
    eventsCtaWrap: {
      marginBottom: 0,
    },
    eventsCtaButton: {
      width: '100%',
      maxWidth: '100%',
      cursor: 'pointer',
      padding: 0,
      display: 'block',
      textAlign: 'left',
      borderRadius: isMobile ? 18 : 22,
      overflow: 'hidden',
      position: 'relative',
      minHeight: isMobile ? 108 : 118,
      background: isDarkTheme
        ? isGuruDomain
          ? 'linear-gradient(180deg, rgba(255,248,230,0.09) 0%, rgba(255,248,230,0.045) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.095) 0%, rgba(255,255,255,0.045) 100%)'
        : isGuruDomain
          ? 'linear-gradient(180deg, rgba(255,252,245,0.95) 0%, rgba(255,250,240,0.88) 100%)'
          : 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(248,250,255,0.88) 100%)',
      border: `1px solid ${
        isGuruDomain
          ? isDarkTheme
            ? 'rgba(218,180,90,0.28)'
            : 'rgba(136,113,43,0.22)'
          : isDarkTheme
            ? 'rgba(255,255,255,0.11)'
            : 'rgba(99,102,241,0.14)'
      }`,
      backdropFilter: isSamsungBrowser ? 'none' : 'blur(22px) saturate(150%)',
      WebkitBackdropFilter: isSamsungBrowser ? 'none' : 'blur(22px) saturate(150%)',
      boxShadow: isDarkTheme
        ? '0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)'
        : '0 8px 28px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.95)',
      isolation: 'isolate',
      transition:
        'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.22s ease, background 0.22s ease',
    },
    eventsCtaShimmerHost: {
      position: 'absolute',
      inset: 0,
      borderRadius: isMobile ? 18 : 22,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 1,
    },
    eventsCtaHighlightLine: {
      position: 'absolute',
      left: 16,
      right: 16,
      top: 0,
      height: 1,
      borderRadius: 1,
      pointerEvents: 'none',
      zIndex: 5,
      opacity: isDarkTheme ? 0.55 : 0.45,
      background: isGuruDomain
        ? 'linear-gradient(90deg, transparent, rgba(255,214,120,0.85), transparent)'
        : 'linear-gradient(90deg, transparent, rgba(165,180,252,0.9), transparent)',
    },
    eventsCtaInner: {
      position: 'relative',
      zIndex: 2,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      padding: isMobile ? '18px 16px 18px 18px' : '20px 22px',
      minHeight: isMobile ? 108 : 118,
      boxSizing: 'border-box',
    },
    eventsCtaEyebrow: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      marginBottom: 6,
      color: isGuruDomain
        ? isDarkTheme
          ? 'rgba(255,220,150,0.65)'
          : 'rgba(136,113,43,0.75)'
        : isDarkTheme
          ? 'rgba(165,180,252,0.75)'
          : 'rgba(99,102,241,0.7)',
    },
    eventsCtaTitle: {
      margin: 0,
      marginBottom: 4,
      fontSize: isMobile ? 19 : 21,
      fontWeight: 600,
      letterSpacing: '-0.03em',
      lineHeight: 1.18,
      color: isDarkTheme ? '#f8fafc' : themeVars.fg,
    },
    eventsCtaDesc: {
      margin: 0,
      fontSize: isMobile ? 14 : 15,
      fontWeight: 400,
      lineHeight: 1.45,
      color: isDarkTheme ? 'rgba(226,232,240,0.82)' : themeVars.smallText,
    },
    eventsCtaChevron: {
      flexShrink: 0,
      width: isMobile ? 42 : 46,
      height: isMobile ? 42 : 46,
      borderRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? 22 : 24,
      fontWeight: 500,
      lineHeight: 1,
      color: isGuruDomain
        ? isDarkTheme
          ? 'rgba(255,252,240,0.96)'
          : '#4a3d12'
        : isDarkTheme
          ? 'rgba(248,250,255,0.98)'
          : '#312e81',
      background: isGuruDomain
        ? isDarkTheme
          ? 'linear-gradient(155deg, rgba(255,228,160,0.52) 0%, rgba(255,190,90,0.28) 52%, rgba(200,150,70,0.18) 100%)'
          : 'linear-gradient(155deg, rgba(255,240,200,0.98) 0%, rgba(255,214,130,0.72) 100%)'
        : isDarkTheme
          ? 'linear-gradient(155deg, rgba(180,190,255,0.55) 0%, rgba(129,140,248,0.38) 48%, rgba(79,70,229,0.22) 100%)'
          : 'linear-gradient(155deg, rgba(224,231,255,0.98) 0%, rgba(165,180,252,0.72) 100%)',
      border: `1px solid ${
        isGuruDomain
          ? isDarkTheme
            ? 'rgba(255,236,200,0.65)'
            : 'rgba(180,150,70,0.45)'
          : isDarkTheme
            ? 'rgba(210,219,255,0.62)'
            : 'rgba(99,102,241,0.42)'
      }`,
      boxShadow: isGuruDomain
        ? isDarkTheme
          ? '0 0 28px rgba(255,200,90,0.35), inset 0 1px 0 rgba(255,255,255,0.42)'
          : '0 6px 22px rgba(136,113,43,0.22), inset 0 1px 0 rgba(255,255,255,0.95)'
        : isDarkTheme
          ? '0 0 32px rgba(129,140,248,0.42), inset 0 1px 0 rgba(255,255,255,0.38)'
          : '0 6px 24px rgba(99,102,241,0.2), inset 0 1px 0 rgba(255,255,255,0.92)',
      backdropFilter: isSamsungBrowser ? 'none' : 'blur(12px) saturate(165%)',
      WebkitBackdropFilter: isSamsungBrowser ? 'none' : 'blur(12px) saturate(165%)',
    },
    featureGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
      gap: isMobile ? 10 : 10,
    },
    cardButton: {
      background: iosCardBaseBg,
      border: `0.5px solid ${isDarkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)'}`,
      padding: isMobile ? '13px 14px' : '14px 16px',
      width: '100%',
      maxWidth: '100%',
      minHeight: isMobile ? 80 : 88,
      height: 'auto',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 14,
      textAlign: 'left',
      cursor: 'pointer',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      borderRadius: 16,
      backdropFilter: isSamsungBrowser ? 'none' : 'blur(18px)',
      WebkitBackdropFilter: isSamsungBrowser ? 'none' : 'blur(18px)',
      transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, background 0.2s ease',
      boxShadow: isDarkTheme
        ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
        : 'inset 0 1px 0 rgba(255,255,255,0.65), 0 1px 2px rgba(0,0,0,0.04)',
    },
    cardIconWrap: {
      width: 48,
      height: 48,
      borderRadius: 12,
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isGuruDomain
        ? isDarkTheme
          ? 'rgba(255,220,150,0.12)'
          : 'rgba(136,113,43,0.12)'
        : isDarkTheme
          ? 'rgba(129,140,248,0.18)'
          : 'rgba(99,102,241,0.12)',
    },
    cardIcon: {
      width: 26,
      height: 26,
      marginBottom: 0,
      objectFit: 'contain',
      filter: iconFilter,
      transition: 'transform 0.22s ease',
    },
    cardTextCol: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    cardTitle: {
      fontSize: isMobile ? '0.97rem' : '1.05rem',
      fontWeight: 600,
      margin: 0,
      marginBottom: 2,
      lineHeight: 1.22,
      letterSpacing: '-0.02em',
      color: isGuruDomain
        ? isDarkTheme
          ? '#f8f4e8'
          : COLORS.cgGold
        : isDarkTheme
          ? '#f8fafc'
          : themeVars.fg,
    },
    cardDesc: {
      fontSize: isMobile ? '0.8rem' : '0.84rem',
      color: themeVars.smallText,
      opacity: isDarkTheme ? 0.8 : 0.82,
      lineHeight: 1.4,
      margin: 0,
      maxWidth: '100%',
    },
  };

  const globalStyles = `
    ${globalAnimationStyles}
    @keyframes landing-events-cta-shine {
      from { transform: translate3d(-115%, 0, 0) skewX(-9deg); }
      to { transform: translate3d(265%, 0, 0) skewX(-9deg); }
    }
    .landing-events-cta-shimmer-beam {
      will-change: transform;
      animation: landing-events-cta-shine 3.4s linear infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .landing-events-cta-shimmer-beam {
        animation: none !important;
      }
    }
    .landing-events-cta:focus-visible {
      outline: 2px solid ${isGuruDomain ? 'rgba(218,180,90,0.85)' : 'rgba(129,140,248,0.85)'};
      outline-offset: 3px;
    }
    button.card-button:hover img {
      transform: scale(1.04);
    }
    button.card-button {
      transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease, background 0.2s ease;
    }
  `;

  return (
    <>
      <ParticleBackground theme={theme} />

      {!isMobile && theme !== 'dark' && (
        <div style={{
          position: 'fixed',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          width: '100%',
          background: isSamsungBrowser ? themeVars.panelBg : COLORS.panelBg,
          backdropFilter: isSamsungBrowser ? 'none' : 'blur(28px)',
          WebkitBackdropFilter: isSamsungBrowser ? 'none' : 'blur(28px)',
          borderLeft: `1px solid ${COLORS.panelBorder}`,
          borderRight: `1px solid ${COLORS.panelBorder}`,
          zIndex: 0,
          pointerEvents: 'none',
        }} />
      )}

      <div style={isMobile ? containerStyles.outer : { ...containerStyles.outer, position: 'relative', zIndex: 1 }}>
        <div
          style={{
            ...containerStyles.inner,
            padding: isMobile ? '22px 16px 32px' : '14px clamp(16px, 3vw, 48px) 52px',
          }}
        >
          <div key={lang}>

            <div style={styles.landingAboveFold}>
              <div ref={heroBleedRef} style={styles.heroBleed}>
                <div
                  style={{
                    ...styles.heroCanvasHost,
                    filter: heroCanvasHoverBlur ? 'blur(0.35px)' : 'none',
                    transition: 'filter 0.38s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <HeroNetworkCanvas
                    anchorRef={heroBleedRef}
                    graphSlotRef={heroVisualRef}
                    theme={theme}
                    isMobile={isMobile}
                    isGuruDomain={isGuruDomain}
                    isSamsungBrowser={isSamsungBrowser}
                  />
                </div>
                <div style={styles.heroTopGlow} aria-hidden />
                <div style={styles.heroInnerRow} className="animate-on-mount">
                  <div style={styles.heroCopy}>
                    <h1 style={styles.heroTitle}>{text.heroTitle}</h1>
                    <p style={styles.heroLead}>{text.heroLead}</p>
                  </div>
                  <div ref={heroVisualRef} style={styles.heroVisual} aria-hidden>
                    {!isMobile && heroHoverFinePointer && !heroReduceMotion && (
                      <div
                        style={styles.heroVisualHoverShield}
                        onMouseEnter={() => setHeroVisualHovered(true)}
                        onMouseLeave={() => setHeroVisualHovered(false)}
                        aria-hidden
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <section style={styles.secondScreen} className="animate-on-mount">
              <StatsCounter
                lang={lang}
                isGuruDomain={isGuruDomain}
                isMobile={isMobile}
                theme={theme}
                themeVars={themeVars}
                outerMarginBottom={0}
              />

              <div style={styles.secondScreenBelow}>
              <div style={styles.eventsCtaWrap}>
                <button
                  type="button"
                  className="landing-events-cta"
                  style={styles.eventsCtaButton}
                  onClick={() => navigate('/events')}
                  aria-label={`${text.eventsTitle}. ${text.eventsDesc}`}
                  onMouseEnter={(e) => {
                    const el = e.currentTarget;
                    el.style.transform = 'translateY(-2px)';
                    el.style.boxShadow = isDarkTheme
                      ? '0 8px 32px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.1)'
                      : '0 12px 36px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,0.98)';
                  }}
                  onMouseLeave={(e) => {
                    const el = e.currentTarget;
                    el.style.transform = 'translateY(0)';
                    el.style.boxShadow = isDarkTheme
                      ? '0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)'
                      : '0 8px 28px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255,255,255,0.95)';
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(0.992)';
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onTouchStart={(e) => {
                    e.currentTarget.style.transform = 'translateY(0) scale(0.992)';
                  }}
                  onTouchEnd={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={styles.eventsCtaShimmerHost} aria-hidden>
                    <div
                      className="landing-events-cta-shimmer-beam"
                      style={{
                        position: 'absolute',
                        top: '-22%',
                        bottom: '-22%',
                        width: '52%',
                        left: '-58%',
                        background: isDarkTheme
                          ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 16%, rgba(255,255,255,0.035) 44%, rgba(255,255,255,0.095) 50%, rgba(255,255,255,0.035) 56%, rgba(255,255,255,0) 84%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0) 12%, rgba(255,255,255,0.22) 46%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.22) 54%, rgba(255,255,255,0) 88%, transparent 100%)',
                        filter: 'blur(16px)',
                        opacity: isDarkTheme ? 0.4 : 0.24,
                        mixBlendMode: isDarkTheme ? 'soft-light' : 'overlay',
                      }}
                    />
                  </div>
                  <div style={styles.eventsCtaHighlightLine} aria-hidden />
                  <div style={styles.eventsCtaInner}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.eventsCtaEyebrow}>{text.eventsCtaHint}</div>
                      <h2 style={styles.eventsCtaTitle}>{text.eventsTitle}</h2>
                      <p style={styles.eventsCtaDesc}>{text.eventsDesc}</p>
                    </div>
                    <div style={styles.eventsCtaChevron} aria-hidden>›</div>
                  </div>
                </button>
              </div>

              <div style={styles.featureGrid}>
                {text.cards.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => navigate(item.path)}
                    className="animate-on-mount card-button"
                    style={styles.cardButton}
                    {...cardHandlers}
                  >
                    <div style={styles.cardIconWrap}>
                      <img src={item.icon} alt="" style={styles.cardIcon} aria-hidden />
                    </div>
                    <div style={styles.cardTextCol}>
                      <h3 style={styles.cardTitle}>{item.title}</h3>
                      <p style={styles.cardDesc}>{item.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              </div>
            </section>

          </div>
        </div>
      </div>

      <style jsx global>{globalStyles}</style>
    </>
  );
}